import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { X, ChevronRight, ChevronLeft, CalendarPlus, Clock, AlertTriangle, Sparkles, Gift } from 'lucide-react';

const TOUR_STEP_CONFIG = [
  { id: 'welcome', icon: Sparkles, targetTestId: null, position: 'center' },
  { id: 'hub', icon: CalendarPlus, targetTestId: 'today-card-top', position: 'below' },
  { id: 'create_event', icon: CalendarPlus, targetTestId: 'create-event-btn', position: 'below' },
  { id: 'holds', icon: Clock, targetTestId: 'view-all-holds-btn', position: 'below' },
  { id: 'conflicts', icon: AlertTriangle, targetTestId: 'week-card-top', position: 'below' },
  { id: 'done', icon: Gift, targetTestId: null, position: 'center' },
];

const STEP_KEYS = {
  welcome: { title: 'onboarding.welcomeTitle', desc: 'onboarding.welcomeDesc' },
  hub: { title: 'onboarding.hubTitle', desc: 'onboarding.hubDesc' },
  create_event: { title: 'onboarding.createEventTitle', desc: 'onboarding.createEventDesc' },
  holds: { title: 'onboarding.holdsTitle', desc: 'onboarding.holdsDesc' },
  conflicts: { title: 'onboarding.conflictsTitle', desc: 'onboarding.conflictsDesc' },
  done: { title: 'onboarding.doneTitle', desc: 'onboarding.doneDesc' },
};

const OnboardingTour = ({ onComplete }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const current = TOUR_STEP_CONFIG[step];

  const handleComplete = useCallback(() => {
    localStorage.setItem('planblox_onboarding_done', 'true');
    onComplete();
  }, [onComplete]);

  const findTarget = useCallback(() => {
    if (!current.targetTestId) {
      setTargetRect(null);
      return;
    }
    const el = document.querySelector(`[data-testid="${current.targetTestId}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      setTargetRect(null);
    }
  }, [current.targetTestId]);

  useEffect(() => {
    findTarget();
    const onScroll = () => findTarget();
    window.addEventListener('resize', findTarget);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', findTarget);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [findTarget]);

  const next = () => {
    if (step < TOUR_STEP_CONFIG.length - 1) setStep(step + 1);
    else handleComplete();
  };

  const back = () => {
    if (step > 0) setStep(step - 1);
  };

  const Icon = current.icon;
  const keys = STEP_KEYS[current.id];
  const isCenter = current.position === 'center';
  const isLast = step === TOUR_STEP_CONFIG.length - 1;

  // For the last card, override desc with credits/invite messaging
  const descText = isLast
    ? t('onboarding.doneDesc', "You're all set! Earn credits by inviting other planners to join PlanBlox. Share your referral code from your Account settings to get started.")
    : t(keys.desc);

  // Calculate card position to stay visible
  const getCardStyle = () => {
    if (isCenter || !targetRect) return undefined;
    const cardHeight = 280;
    const viewportH = window.innerHeight;
    const below = targetRect.top + targetRect.height + 20;
    // If card would overflow bottom, place it above the target
    if (below + cardHeight > viewportH) {
      return { bottom: viewportH - targetRect.top + 20 };
    }
    return { top: below };
  };

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="onboarding-overlay">
      {/* Dark overlay without blur so content stays readable */}
      <div className="absolute inset-0 bg-black/50" onClick={handleComplete} />

      {/* Spotlight cutout around target element */}
      {targetRect && (
        <div
          className="absolute rounded-xl pointer-events-none"
          style={{
            left: targetRect.left - 8,
            top: targetRect.top - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
            border: '2px solid rgba(255,255,255,0.9)',
            zIndex: 1,
          }}
        />
      )}

      {/* Tour Card */}
      <div
        className={`absolute z-10 ${isCenter
          ? 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
          : 'left-1/2 -translate-x-1/2'
        }`}
        style={!isCenter ? getCardStyle() : undefined}
        data-testid="onboarding-card"
      >
        <Card className="w-[340px] sm:w-[400px] shadow-2xl border-0 bg-white">
          <CardContent className="p-6">
            {/* Close button */}
            <button
              className="absolute top-3 right-3 p-1 rounded-full hover:bg-muted transition-colors"
              onClick={handleComplete}
              data-testid="onboarding-close-btn"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>

            {/* Step indicator */}
            <div className="flex gap-1.5 mb-4">
              {TOUR_STEP_CONFIG.map((_, i) => (
                <div
                  key={i}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${
                    i <= step ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>

            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className={`p-3 rounded-2xl ${isLast ? 'bg-emerald-50' : isCenter ? 'bg-primary/10' : 'bg-blue-50'}`}>
                <Icon className={`h-8 w-8 ${isLast ? 'text-emerald-600' : isCenter ? 'text-primary' : 'text-blue-600'}`} />
              </div>
            </div>

            {/* Content */}
            <h3 className="text-lg font-semibold text-center mb-2 text-slate-900" data-testid="onboarding-title">
              {t(keys.title)}
            </h3>
            <p className="text-sm text-slate-500 text-center mb-6 leading-relaxed" data-testid="onboarding-desc">
              {descText}
            </p>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <div>
                {step > 0 ? (
                  <Button variant="ghost" size="sm" onClick={back} data-testid="onboarding-back-btn">
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    {t('onboarding.back')}
                  </Button>
                ) : (
                  <Button variant="ghost" size="sm" onClick={handleComplete} className="text-muted-foreground" data-testid="onboarding-skip-btn">
                    {t('onboarding.skip')}
                  </Button>
                )}
              </div>
              <Button size="sm" onClick={next} data-testid="onboarding-next-btn">
                {isLast ? t('onboarding.getStarted') : t('onboarding.next')}
                {!isLast && <ChevronRight className="h-4 w-4 ml-1" />}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OnboardingTour;
